import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import Header from './Header';
import PageHeader from './ui/PageHeader';
import PlanCardsSection from './myplan/PlanCardsSection';
import { useAdmin } from '../contexts/AdminContext';
import { useSidebar } from '../contexts/SidebarContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { formatPlanDate } from '../utils/planDate';
import { getDeviceFp } from '../utils/deviceFp';
import {
  CreditCard,
  Check,
  Zap,
  Headphones,
  ExternalLink,
  AudioLines,
  XCircle,
  AlertTriangle,
  RotateCcw,
  ChevronDown,
  HelpCircle,
} from 'lucide-react';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

const WHATSAPP_SUPPORT_LINK = 'https://wa.me/5577988744698?text=Olá!%20Preciso%20de%20ajuda%20com%20minha%20assinatura%20no%20Dark%20Planner.';

/* ─── Helpers ─── */
const formatDate = (dateStr) => formatPlanDate(dateStr) || '—';

// Badge pelo ESTADO EFETIVO (mesma verdade do painel admin); labels via i18n.
const ESTADO_BADGE = {
  PREMIUM:     { color: '#34D399', bg: 'rgba(52, 211, 153, 0.10)', border: 'rgba(52, 211, 153, 0.20)' },
  CANCELADO:   { color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.10)', border: 'rgba(245, 158, 11, 0.20)' },
  REEMBOLSADO: { color: '#F87171', bg: 'rgba(248, 113, 113, 0.10)', border: 'rgba(248, 113, 113, 0.20)' },
  FREE:        { color: '#A0A0A0', bg: 'rgba(160, 160, 160, 0.10)', border: 'rgba(160, 160, 160, 0.20)' },
};

const cardStyle = {
  background: 'var(--dp-bg-card)',
  border: '1px solid var(--dp-border-subtle)',
  borderRadius: '14px',
  padding: '24px 28px',
};

// Ordem de exibição (q6 = "Quando posso cancelar minha assinatura?" vem PRIMEIRO,
// pedido do owner). As chaves i18n myplan.faq.* permanecem estáveis.
const FAQ_ITEMS = ['q6', 'q1', 'q2', 'q3', 'q4', 'q5', 'q7', 'q8', 'q9'];

/* ═══ FAQ accordion (perguntas da página de vendas + downgrade/cancelamento) ═══ */
const FaqSection = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(null);
  return (
    <div className="dp-top-accent" style={{ ...cardStyle, marginBottom: '24px', animation: 'dp-fadeInUp 0.5s ease' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
        <HelpCircle size={18} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
        <span style={{ fontSize: '16px', fontWeight: 700, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
          {t('myplan.faq_title')}
        </span>
      </div>
      {FAQ_ITEMS.map((q, i) => {
        const isOpen = open === i;
        return (
          <div key={q} style={{ borderBottom: i < FAQ_ITEMS.length - 1 ? '1px solid var(--dp-border-subtle)' : 'none' }}>
            <button
              onClick={() => setOpen(isOpen ? null : i)}
              aria-expanded={isOpen}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                padding: '15px 2px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
              }}
            >
              <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', lineHeight: 1.4 }}>
                {t(`myplan.faq.${q}`)}
              </span>
              <ChevronDown
                size={16}
                style={{
                  color: 'var(--dp-text-tertiary)', flexShrink: 0,
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            </button>
            {isOpen && (
              <p style={{
                fontSize: '13px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)',
                lineHeight: 1.65, margin: '0 0 15px', paddingRight: '28px',
                animation: 'dp-fadeInUp 0.2s ease',
              }}>
                {t(`myplan.faq.a${q.slice(1)}`)}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ═══════════════════════════════════════════ */
const MyPlanPage = ({ onLogout }) => {
  usePageTitle('Meu Plano');
  const { t } = useTranslation();
  const { subscriptionData, isLoading, isSuperAdmin } = useAdmin();
  const { sidebarWidth, isMobile } = useSidebar();

  const planType = subscriptionData?.plan_type?.toLowerCase() || '';
  const isTrial = planType === 'trial';
  const isAdmin = isSuperAdmin;
  const hasPaidPlan = subscriptionData?.has_subscription && !isTrial && !isAdmin;
  const isFreeState = !hasPaidPlan && !isAdmin;

  // ─── Estado de cancelamento ────────────────────────────────────
  // webhookCancelled: a Cakto JÁ confirmou (auto_renew off) → "Plano cancelado".
  // requestPending: usuário solicitou AQUI e o admin ainda não processou na Cakto.
  // localCancel sobrepõe o valor do contexto logo após o POST/DELETE (sem refetch global).
  const [localCancel, setLocalCancel] = useState(null);
  const webhookCancelled = !!subscriptionData && hasPaidPlan
    && (subscriptionData.auto_renew === false || subscriptionData.cancellation_requested === true);
  const requestPending = !webhookCancelled
    && (localCancel != null ? localCancel : !!subscriptionData?.cancel_request_pending);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const accessUntil = formatDate(subscriptionData?.end_date || subscriptionData?.next_charge_date);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const handleConfirmCancel = async () => {
    setCancelLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/subscription/cancel-request`, {}, authHeaders());
      setLocalCancel(true);
      setShowCancelModal(false);
      toast.success(t('myplan.toast_requested'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('myplan.toast_error'));
    } finally {
      setCancelLoading(false);
    }
  };

  const handleWithdrawCancel = async () => {
    setCancelLoading(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/subscription/cancel-request`, authHeaders());
      setLocalCancel(false);
      toast.success(t('myplan.toast_withdrawn'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('myplan.toast_error'));
    } finally {
      setCancelLoading(false);
    }
  };

  // ─── Uso de áudios (mesmo endpoint e headers do gerador) ───────
  // X-Device-FP é obrigatório p/ usuário FREE: sem ele o /usage é fail-closed
  // (antifraude) e devolveria o contador como esgotado.
  const [usage, setUsage] = useState(null);
  const fetchUsage = useCallback(async () => {
    try {
      const deviceFp = await getDeviceFp();
      const res = await axios.get(`${BACKEND_URL}/api/audio-tts/usage`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'X-Device-FP': deviceFp || '',
        },
      });
      setUsage(res.data);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  /* ─── Loading ─── */
  if (isLoading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
        <Sidebar onLogout={onLogout} />
        <div style={{ marginLeft: isMobile ? 0 : `${sidebarWidth}px`, flex: 1, minWidth: 0, transition: 'margin-left 0.3s ease', display: 'flex', flexDirection: 'column' }}>
          <Header />
          <main className="dp-main-content" style={{ marginTop: '60px', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
              <div className="dp-skeleton" style={{ width: '200px', height: '24px' }} />
              <div className="dp-skeleton" style={{ width: '300px', height: '16px' }} />
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ─── Card A: uso de áudios ─── */
  const renderUsageCard = () => {
    const unlimited = usage?.unlimited || usage?.limit === -1;
    // O endpoint /api/audio-tts/usage devolve "used_today" (não "used").
    const used = usage?.used_today ?? 0;
    const limit = usage?.limit;
    const pct = !unlimited && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
    const chars = usage?.character_limit;
    const charsLabel = (chars && chars !== -1) ? Number(chars).toLocaleString('pt-BR') : '∞';

    return (
      <div className="dp-top-accent" style={{ ...cardStyle, animation: 'dp-fadeInUp 0.4s ease' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AudioLines size={16} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
              {t('myplan.usage_title')}
            </span>
          </div>
          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', fontVariantNumeric: 'tabular-nums' }}>
            {usage == null
              ? '…'
              : unlimited
                ? `⚡ ${t('myplan.usage_unlimited')}`
                : t('myplan.usage_of', { used, limit })}
          </span>
        </div>

        {/* Barra de progresso */}
        <div style={{ height: '8px', borderRadius: '999px', background: 'var(--dp-bg-secondary)', overflow: 'hidden', marginBottom: '12px' }}>
          <div style={{
            height: '100%', width: unlimited ? '100%' : `${pct}%`,
            background: 'linear-gradient(90deg, #0DAEFF 0%, #3B82F6 100%)',
            borderRadius: '999px', transition: 'width 0.4s ease',
            opacity: unlimited ? 0.35 : 1,
          }} />
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
          <span style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)' }}>
            {t('myplan.usage_chars', { chars: charsLabel })}
          </span>
          <span style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)' }}>
            {usage?.is_free_lifetime ? t('myplan.usage_free_note') : t('myplan.usage_renews')}
          </span>
        </div>
      </div>
    );
  };

  /* ─── Card B: plano atual ─── */
  const renderPlanCard = () => {
    const estado = isAdmin ? 'PREMIUM' : (subscriptionData?.estado_efetivo || (hasPaidPlan ? 'PREMIUM' : 'FREE'));
    const badge = ESTADO_BADGE[estado] || ESTADO_BADGE.FREE;
    const badgeLabel = isAdmin ? t('myplan.estado.ADMIN') : t(`myplan.estado.${estado}`, { defaultValue: estado });
    const displayName = isAdmin
      ? t('myplan.admin_access')
      : hasPaidPlan
        ? planType.toUpperCase()
        : t('myplan.free_plan_name');

    return (
      <div className="dp-top-accent" style={{ ...cardStyle, animation: 'dp-fadeInUp 0.44s ease', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
            {t('myplan.current_plan')}
          </span>
          <span style={{
            fontSize: '11px', fontWeight: 600, color: badge.color, background: badge.bg,
            padding: '3px 10px', borderRadius: '9999px', border: `1px solid ${badge.border}`,
            fontFamily: 'var(--dp-font)',
          }}>
            {badgeLabel}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', marginBottom: hasPaidPlan ? '14px' : '4px' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: 'rgba(13, 174, 255, 0.06)',
            padding: '9px 18px', borderRadius: '11px',
            border: '1px solid rgba(13, 174, 255, 0.10)',
          }}>
            <Zap size={17} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
            <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
              {displayName}
            </span>
          </div>
          {hasPaidPlan && !webhookCancelled && !requestPending && (
            <div style={{ fontSize: '12.5px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)' }}>
              {t('myplan.next_charge')}: <strong style={{ color: 'var(--dp-text-primary)' }}>{formatDate(subscriptionData?.next_charge_date || subscriptionData?.end_date)}</strong>
            </div>
          )}
        </div>

        {isFreeState && (
          <p style={{ fontSize: '13px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)', margin: '0 0 4px', lineHeight: 1.55 }}>
            {t('myplan.free_state_text')}
          </p>
        )}

        {/* Banner: cancelamento solicitado (aguardando processamento) */}
        {hasPaidPlan && requestPending && (
          <div style={{
            background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.20)',
            borderRadius: '10px', padding: '12px 14px', marginBottom: '12px',
            display: 'flex', alignItems: 'flex-start', gap: '10px',
          }}>
            <AlertTriangle size={15} style={{ color: '#F59E0B', flexShrink: 0, marginTop: '2px' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#F59E0B', fontFamily: 'var(--dp-font)', marginBottom: '3px' }}>
                {t('myplan.cancel_requested_title')}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)', lineHeight: 1.5 }}>
                {t('myplan.cancel_requested_text', { date: accessUntil })}
              </div>
            </div>
            <button
              onClick={handleWithdrawCancel}
              disabled={cancelLoading}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px',
                padding: '6px 12px', borderRadius: '8px', flexShrink: 0,
                background: 'transparent', border: '1px solid var(--dp-border-medium)',
                color: 'var(--dp-text-secondary)', fontSize: '11.5px', fontWeight: 600,
                fontFamily: 'var(--dp-font)', cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              <RotateCcw size={12} />
              {t('myplan.withdraw_button')}
            </button>
          </div>
        )}

        {/* Banner: cancelamento JÁ confirmado no gateway */}
        {hasPaidPlan && webhookCancelled && (
          <div style={{
            background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.20)',
            borderRadius: '10px', padding: '12px 14px', marginBottom: '12px',
            display: 'flex', alignItems: 'flex-start', gap: '10px',
          }}>
            <XCircle size={15} style={{ color: '#F59E0B', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#F59E0B', fontFamily: 'var(--dp-font)', marginBottom: '3px' }}>
                {t('myplan.cancelled_title')}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)', lineHeight: 1.5 }}>
                {t('myplan.cancelled_text', { date: accessUntil })}
              </div>
            </div>
          </div>
        )}

        {/* Ações */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: 'auto', paddingTop: '6px' }}>
          {hasPaidPlan && !requestPending && !webhookCancelled && (
            <button
              onClick={() => setShowCancelModal(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '8px 16px', borderRadius: '10px',
                background: 'transparent', border: '1px solid rgba(239, 68, 68, 0.30)',
                color: '#EF4444', fontSize: '12.5px', fontWeight: 600,
                fontFamily: 'var(--dp-font)', cursor: 'pointer', whiteSpace: 'nowrap',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <XCircle size={14} />
              {t('myplan.cancel_button')}
            </button>
          )}
          <button
            onClick={() => window.open(WHATSAPP_SUPPORT_LINK, '_blank')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', borderRadius: '10px',
              background: 'transparent', border: '1px solid var(--dp-border-medium)',
              color: 'var(--dp-text-secondary)', fontSize: '12.5px', fontWeight: 600,
              fontFamily: 'var(--dp-font)', cursor: 'pointer', whiteSpace: 'nowrap',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dp-bg-secondary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <Headphones size={13} />
            {t('myplan.support_button')}
          </button>
        </div>
        {hasPaidPlan && !requestPending && !webhookCancelled && (
          <div style={{ fontSize: '11.5px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', marginTop: '10px', lineHeight: 1.5 }}>
            {t('myplan.cancel_note')}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <Sidebar onLogout={onLogout} />

      <div style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        flex: 1,
        minWidth: 0,
        transition: 'margin-left 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Header />

        <main className="dp-main-content" style={{
          marginTop: '60px',
          flex: 1,
          animation: 'dp-pageFadeIn 0.35s ease',
        }}>
          <PageHeader
            icon={<CreditCard size={20} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />}
            title={t('myplan.title')}
            description={t('myplan.subtitle')}
          />

          {/* ═══ Linha 1: uso de áudios + plano atual (estilo assinatura ElevenLabs) ═══ */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1.4fr',
            gap: '16px',
            marginBottom: '28px',
            alignItems: 'stretch',
          }}>
            {renderUsageCard()}
            {renderPlanCard()}
          </div>

          {/* ═══ Linha 2: os MESMOS cards de planos da landing (fonte única) ═══ */}
          <PlanCardsSection subscriptionData={subscriptionData} isSuperAdmin={isAdmin} />

          {/* ═══ Linha 3: FAQ (perguntas da página de vendas + downgrade) ═══ */}
          <FaqSection />

          {/* ═══ Suporte ═══ */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            padding: '16px 0',
            borderTop: '1px solid var(--dp-border-subtle)',
            marginTop: '8px',
          }}>
            <span style={{ fontSize: '13px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)' }}>
              {t('myplan.need_help')}
            </span>
            <button
              onClick={() => window.open(WHATSAPP_SUPPORT_LINK, '_blank')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px',
                padding: '6px 14px', borderRadius: '8px',
                background: 'transparent',
                border: '1px solid var(--dp-border-medium)',
                color: 'var(--dp-blue-500, #0DAEFF)',
                fontSize: '13px', fontWeight: 500,
                fontFamily: 'var(--dp-font)',
                cursor: 'pointer', transition: 'all 0.2s ease',
              }}
            >
              <Headphones size={14} />
              {t('myplan.talk_support')}
              <ExternalLink size={11} />
            </button>
          </div>

          {/* ─── Modal: confirmar cancelamento ─── */}
          {showCancelModal && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.60)', backdropFilter: 'blur(4px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 9999,
              animation: 'dp-pageFadeIn 0.2s ease',
            }}
              onMouseDown={(e) => { if (e.target === e.currentTarget) setShowCancelModal(false); }}
            >
              <div
                style={{
                  background: 'var(--dp-bg-card)',
                  border: '1px solid var(--dp-border-subtle)',
                  borderRadius: '16px',
                  padding: '32px',
                  maxWidth: '500px',
                  width: '90%',
                  animation: 'dp-fadeInUp 0.3s ease',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '10px',
                    background: 'rgba(239, 68, 68, 0.10)',
                    border: '1px solid rgba(239, 68, 68, 0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <XCircle size={20} style={{ color: '#EF4444' }} />
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                    {t('myplan.modal_title')}
                  </div>
                </div>

                <p style={{ fontSize: '13.5px', color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', lineHeight: 1.6, margin: '0 0 14px' }}>
                  {t('myplan.modal_question', { plan: planType.toUpperCase() })}
                </p>

                <div style={{
                  background: 'rgba(13, 174, 255, 0.05)',
                  border: '1px solid rgba(13, 174, 255, 0.15)',
                  borderRadius: '10px',
                  padding: '12px 14px',
                  marginBottom: '12px',
                  display: 'flex', alignItems: 'flex-start', gap: '10px',
                }}>
                  <Check size={15} style={{ color: 'var(--dp-blue-500, #0DAEFF)', flexShrink: 0, marginTop: '2px' }} />
                  <span style={{ fontSize: '12.5px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)', lineHeight: 1.6 }}>
                    {t('myplan.modal_info', { date: accessUntil })}
                  </span>
                </div>

                <p style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', lineHeight: 1.55, margin: '0 0 22px' }}>
                  {t('myplan.modal_processing_note')}
                </p>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => setShowCancelModal(false)}
                    style={{
                      flex: 1,
                      padding: '11px 18px', borderRadius: '10px',
                      background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
                      color: '#fff', border: 'none',
                      fontSize: '13px', fontWeight: 600,
                      fontFamily: 'var(--dp-font)',
                      cursor: 'pointer',
                    }}
                  >
                    {t('myplan.modal_keep')}
                  </button>
                  <button
                    onClick={handleConfirmCancel}
                    disabled={cancelLoading}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      padding: '11px 18px', borderRadius: '10px',
                      background: 'transparent',
                      border: '1px solid rgba(239, 68, 68, 0.40)',
                      color: '#EF4444',
                      fontSize: '13px', fontWeight: 600,
                      fontFamily: 'var(--dp-font)',
                      cursor: 'pointer',
                      opacity: cancelLoading ? 0.6 : 1,
                    }}
                  >
                    <XCircle size={14} />
                    {cancelLoading ? '…' : t('myplan.modal_confirm')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default MyPlanPage;
